import { useEffect, useState } from "react";
import {
  View,
  Menu,
  Edit,
  Trash,
  Plus,
  Printer,
  FileDown,
  Upload,
  MoreVertical,
} from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import toast, { Toaster } from "react-hot-toast";

import Header from "../components/Header";
import LeftSidebar from "../components/LeftSidebar";
import RoomEquipmentModal from "../components/RoomEquipmentModal"; // ✅ new modal
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";

import api from "../../connector/api";

export default function ClassroomEquipment() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [actionDropdownOpen, setActionDropdownOpen] = useState(false);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  const [formData, setFormData] = useState({
    id: null,
    description: "",
    condition: "",
    quantity: "",
    location: "",
    responsible_person: "",
  });
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch classroom equipment
  const fetchEquipment = async () => {
    try {
      const res = await api.get("classroom-equipment/"); // ✅ backend endpoint
      setEquipment(res.data);
    } catch (err) {
      toast.error("Failed to fetch classroom equipment");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEquipment();
  }, []);

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (formData.id) {
        await api.put(`classroom-equipment/${formData.id}/`, formData);
        toast.success("Classroom equipment updated!");
      } else {
        await api.post("classroom-equipment/", formData);
        toast.success("Classroom equipment added!");
      }
      fetchEquipment();
      setIsModalOpen(false);
      resetForm();
    } catch (err) {
      toast.error("Error saving classroom equipment");
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    try {
      await api.delete(`classroom-equipment/${id}/`);
      toast.success("Classroom equipment deleted");
      fetchEquipment();
    } catch (err) {
      toast.error("Failed to delete classroom equipment");
    }
  };

  const resetForm = () => {
    setFormData({
      id: null,
      description: "",
      condition: "",
      quantity: "",
      location: "",
      responsible_person: "",
    });
  };

  // Filter by search
  const filteredEquipment = equipment.filter(
    (eq) =>
      eq.description.toLowerCase().includes(search.toLowerCase()) ||
      eq.location.toLowerCase().includes(search.toLowerCase())
  );

  // Pagination
  const totalPages = Math.ceil(filteredEquipment.length / itemsPerPage);
  const currentItems = filteredEquipment.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePrev = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNext = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  // Handle Export
  const handleExport = () => {
    if (!equipment.length) {
      toast.error("No data to export!");
      return;
    }

    const exportData = equipment.map((eq) => ({
      Description: eq.description,
      Condition: eq.condition,
      Quantity: eq.quantity,
      Location: eq.location,
      Responsible: eq.responsible_person_name || "N/A",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Classroom Equipment");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(
      data,
      `Classroom_Equipment_${new Date().toISOString().split("T")[0]}.xlsx`
    );

    toast.success("Excel exported successfully!");
  };

  // Handle Print
  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleString();

    printWindow.document.write(`
      <html>
        <head>
          <title>Classroom Equipment Report</title>
          <link href="https://cdn.jsdelivr.net/npm/tailwindcss@3.3.2/dist/tailwind.min.css" rel="stylesheet">
        </head>
        <body class="p-6">
          <h1 class="text-xl font-bold mb-4">Classroom Equipment Report</h1>
          <p class="text-sm text-gray-700 mb-4">Printed: ${formattedDate}</p>
          <table class="min-w-full border border-gray-300 text-sm">
            <thead>
              <tr>
                <th>Description</th>
                <th>Condition</th>
                <th>Quantity</th>
                <th>Location</th>
                <th>Responsible</th>
              </tr>
            </thead>
            <tbody>
              ${equipment
                .map(
                  (eq) => `
                <tr>
                  <td>${eq.description}</td>
                  <td>${eq.condition}</td>
                  <td>${eq.quantity}</td>
                  <td>${eq.location}</td>
                  <td>${eq.responsible_person_name || "N/A"}</td>
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>
          <footer class="mt-6 text-sm text-gray-500">
            &copy; ${currentDate.getFullYear()} Cambodia Adventist International School
          </footer>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  return (
    <div className="flex flex-col min-h-screen">
      <header className="w-full border-b bg-white shadow">
        <Header />
      </header>

      <div className="flex flex-1">
        {sidebarOpen && (
          <aside className="w-64 border-r bg-gray-50">
            <LeftSidebar />
          </aside>
        )}
        <main className="flex-1 p-4">
          <div className="max-w-8xl mx-auto p-4">
            {/* Header + Action dropdown */}
            <div className="bg-white p-2 rounded shadow flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <button
                  className="p-2 rounded hover:bg-gray-100"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                >
                  <Menu className="h-5 w-5 text-gray-600" />
                </button>
                <h1 className="text-xl font-bold">Classroom Equipment</h1>
              </div>

              {/* Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setActionDropdownOpen(!actionDropdownOpen)}
                  className="p-2 rounded hover:bg-gray-100"
                >
                  <MoreVertical className="h-5 w-5 text-gray-700" />
                </button>

                {actionDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white border rounded-lg shadow-lg z-50">
                    <ul className="py-1">
                      <li>
                        <button
                          onClick={() => {
                            resetForm();
                            setIsModalOpen(true);
                            setActionDropdownOpen(false);
                          }}
                          className="flex items-center w-full px-4 py-2 text-sm text-[#002855] hover:bg-gray-100"
                        >
                          <Plus className="h-4 w-4 mr-2" /> Add Equipment
                        </button>
                      </li>
                      <li>
                        <button
                          onClick={() => {
                            handleExport();
                            setActionDropdownOpen(false);
                          }}
                          className="flex items-center w-full px-4 py-2 text-sm text-green-600 hover:bg-gray-100"
                        >
                          <FileDown className="h-4 w-4 mr-2" /> Export
                        </button>
                      </li>
                      <li>
                        <button
                          onClick={() => {
                            handlePrint();
                            setActionDropdownOpen(false);
                          }}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <Printer className="h-4 w-4 mr-2" /> Print
                        </button>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Search */}
            <input
              type="text"
              placeholder="Search..."
              className="border p-2 rounded w-full mb-4"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
            />

            {/* Table */}
            {loading ? (
              <p>Loading...</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-300 text-sm" id="equipment-table">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border px-4 py-2 text-left">Description</th>
                      <th className="border px-4 py-2 text-left">Quantity</th>
                      <th className="border px-4 py-2 text-left hidden md:table-cell">Condition</th>
                      <th className="border px-4 py-2 text-left hidden lg:table-cell">Classroom</th>
                      <th className="border px-4 py-2 text-left hidden lg:table-cell">Responsible</th>
                      <th className="border px-4 py-2 text-left hidden lg:table-cell">Date</th>
                      <th className="border px-4 py-2 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.length > 0 ? (
                      currentItems.map((eq) => (
                        <tr key={eq.id} className="hover:bg-gray-50">
                          <td className="border px-4 py-2 font-medium">{eq.description}</td>
                          <td className="border px-4 py-2 hidden md:table-cell">{eq.quantity}</td>
                          <td className="border px-4 py-2">{eq.condition}</td>
                          <td className="border px-4 py-2 hidden lg:table-cell">{eq.location}</td>
                          <td className="border px-4 py-2 hidden lg:table-cell">
                            {eq.responsible_person_name || "N/A"}
                          </td>
                          <td className="border px-4 py-2 hidden lg:table-cell">
                            {eq.date_added}
                          </td>
                          <td className="border-t px-4 py-2 flex gap-2">
                            <button
                                onClick={() => {
                                setFormData(eq);
                                setIsModalOpen(true);
                              }}
                              className="bg-yellow-500 text-white px-3 py-1 rounded flex gap-1"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                                onClick={() => {
                                setItemToDelete(eq);
                                setDeleteModalOpen(true);
                              }}
                              className="bg-red-600 text-white px-3 py-1 rounded flex gap-1"
                            >
                              <Trash className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="text-center py-4 text-gray-500">
                          No classroom equipment found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-between items-center mt-2 text-sm">
                    <button
                      onClick={handlePrev}
                      disabled={currentPage === 1}
                      className={`px-3 py-1 rounded ${
                        currentPage === 1 ? "bg-gray-200" : "bg-gray-300 hover:bg-gray-400"
                      }`}
                    >
                      Prev
                    </button>
                    <span>
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={handleNext}
                      disabled={currentPage === totalPages}
                      className={`px-3 py-1 rounded ${
                        currentPage === totalPages ? "bg-gray-200" : "bg-gray-300 hover:bg-gray-400"
                      }`}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
        {/* Modals */}
        <RoomEquipmentModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleSubmit}
          formData={formData}
          setFormData={setFormData}
        />
        <DeleteConfirmationModal
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          itemName={itemToDelete?.description || "this item"}
          onConfirm={() => {
            handleDelete(itemToDelete.id);
            setDeleteModalOpen(false);
          }}
        />
      </div>

      <Toaster position="top-right" />
    </div>
  );
}
